import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, Airplane, MapPin, ForkKnife, CarProfile, Boat, Ticket, Heart, BookmarkSimple, Users, Calendar, Wallet } from "@phosphor-icons/react";
import { PassportLogo } from "@/components/Reveal";
import { conciergeApi } from "@/lib/apiClient";
import { useTranslation } from "react-i18next";

const KIND_META = {
    flight: { icon: Airplane, key: "publicTrip.kinds.flight", color: "#9FD5D0" },
    hotel: { icon: BookmarkSimple, key: "publicTrip.kinds.hotel", color: "#D4AF37" },
    ground: { icon: CarProfile, key: "publicTrip.kinds.ground", color: "#F4A28F" },
    cruise: { icon: Boat, key: "publicTrip.kinds.cruise", color: "#9FD5D0" },
    dining: { icon: ForkKnife, key: "publicTrip.kinds.dining", color: "#F4A28F" },
    activity: { icon: Ticket, key: "publicTrip.kinds.activity", color: "#D4AF37" },
    traveldate: { icon: Heart, key: "publicTrip.kinds.traveldate", color: "#F4A28F" },
};

export default function PublicTrip() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const [trip, setTrip] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        conciergeApi.publicShare(slug).then(setTrip).catch(() => setError(t("publicTrip.privateOrNotShared")));
    }, [slug, t]);
    if (error) return (
        <div className="min-h-screen grid place-items-center text-center px-6">
            <div>
                <div className="text-6xl mb-4">🔒</div>
                <div className="font-serif text-3xl text-white mb-2">{t("publicTrip.notAvailable")}</div>
                <div className="text-white/60 mb-6">{error}</div>
                <Link to="/concierge" className="btn-gold">{t("publicTrip.planOwnTrip")}</Link>
            </div>
        </div>
    );
    if (!trip) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    const totals = trip.totals || {};
    return (
        <div className="min-h-screen grain" data-testid="public-trip">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[1100px] mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">{t("conciergeApp.passport")}</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: "var(--gold)" }}>{t("publicTrip.sharedTripOverline")}</div>
                        </div>
                    </Link>
                    <Link to="/concierge" className="btn-gold text-xs !py-2 !px-4" data-testid="public-plan-cta">
                        <Sparkle size={12} weight="fill" /> {t("publicTrip.planYourOwnTripCTA")}
                    </Link>
                </div>
            </header>

            <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[16/7] mb-8"
                    style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
                    <img src={trip.hero_image} alt={trip.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.2) 0%, rgba(5,10,17,0.9) 100%)" }} />
                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: "var(--gold)" }}>
                            {trip.user_name || t("publicTrip.passportTraveler")} · {t("publicTrip.sharedWithConcierge")}
                        </div>
                        <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1] tracking-tight">
                            {trip.emoji} {trip.title}
                        </h1>
                        <div className="mt-3 flex flex-wrap gap-3 text-white/85 text-sm">
                            {trip.days ? <Meta icon={Calendar} label={t("publicTrip.days", { count: trip.days })} /> : null}
                            {trip.travelers ? <Meta icon={Users} label={t("publicTrip.travelers", { count: (trip.travelers.adults || 1) + (trip.travelers.children || 0) })} /> : null}
                            {totals.total_spent ? <Meta icon={Wallet} label={`~$${Math.round(totals.total_spent).toLocaleString()}`} /> : null}
                        </div>
                    </div>
                </motion.div>

                <div className="grid gap-4 sm:grid-cols-2">
                    {(trip.items || []).map((it) => {
                        const meta = KIND_META[it.kind] || KIND_META.activity;
                        const Icon = meta.icon;
                        return (
                            <div key={it.id} className="rounded-2xl p-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid={`public-item-${it.id}`}>
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-2xl grid place-items-center shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: `1px solid ${meta.color}40` }}>
                                        <Icon size={14} color={meta.color} weight="fill" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-white font-medium leading-tight line-clamp-1">{it.title}</div>
                                        {it.subtitle && <div className="text-white/60 text-[13px] mt-1 line-clamp-2">{it.subtitle}</div>}
                                        <div className="flex items-center gap-2 mt-2 text-[10px] uppercase tracking-[0.15em] text-white/50">
                                            {it.day ? <span>{t("publicTrip.dayNum", { day: it.day })}</span> : null}
                                            {it.city ? <span>· {it.city}</span> : null}
                                            {it.price_estimate ? <span>· ~${Math.round(it.price_estimate)}</span> : null}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-12 text-center rounded-3xl p-8" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.3)" }}>
                    <div className="text-3xl mb-3">✨</div>
                    <div className="font-serif text-3xl text-white mb-2">{t("publicTrip.bottomHeading")}</div>
                    <div className="text-white/60 mb-5 max-w-lg mx-auto">{t("publicTrip.bottomDesc")}</div>
                    <Link to="/concierge" className="btn-gold inline-flex" data-testid="public-plan-cta-bottom">
                        <Sparkle size={12} weight="fill" /> {t("publicTrip.startPlanning")}
                    </Link>
                </div>

                <div className="text-center text-[10px] uppercase tracking-[0.3em] text-white/40 mt-8">
                    {t("publicTrip.disclaimer")}
                </div>
            </div>
        </div>
    );
}

function Meta({ icon: Icon, label }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[12px]">
            <Icon size={12} weight="fill" color="#D4AF37" />
            {label}
        </span>
    );
}
